// The helper's own screen, as pure data (bead hf-e6-commitments-coverage-udt.9).
//
// ── THIS MODULE COMPOSES; IT DOES NOT ASK NEW QUESTIONS ────────────────────────
//
// Every fact here already exists on a landed read model. The asks come from
// `requests.myRequests`, the rechecks from `commitments.myRechecks`, the agreements
// from `commitments.listMyCommitments`, and the windows, labels, coverage states and
// availability rows from the one plan read the shell already subscribes to. Nothing
// below queries anything, and nothing below derives coverage — `deriveCoverage` in
// the kernel owns that, the server ran it, and a second opinion computed in a browser
// is the drift COV-002 forbids.
//
// ── THE HEADLINE TIME IS THE SUPPORT WINDOW (NED-002, invariant 5) ─────────────
//
// A need's window is INDEPENDENT of its event's. The helper agreed to 09:15–13:00
// around a 10:15 appointment, and the number they must read first is 09:15. The event
// is carried alongside as context and worded so it cannot be mistaken for the time to
// turn up. Getting this backwards on the one screen a helper opens every day is how
// somebody arrives two hours late to a hospital.
//
// ── AND A QUESTION IS NEVER ALSO A REASSURANCE ─────────────────────────────────
//
// `myRechecks` returns two kinds of row: commitments already flipped to
// `reconfirmation_required`, AND commitments still marked `accepted` whose binding
// has gone stale. Both are questions. So the agreed-to list is filtered by the
// recheck list's need ids rather than by commitment state — filtering by state alone
// would leave a stale-but-accepted agreement sitting under "You've agreed to" while
// the same thing sat above it asking whether the person still could. One item, two
// contradictory sentences, on the screen least able to afford it.

import { instant, localDateOf, timeZone } from "@herefor/domain-kernel";
import type { commitments as commitmentContracts } from "@herefor/contracts";

import type { LivePlanResult } from "../../convex/plan";
import { formatWindowInZone, userVisibleStateKey } from "../plan/planData";
import {
  availabilityBatchExpiry,
  isWithinAvailabilityRenewalHorizon,
} from "../availability/availabilityData";
import type { UserVisibleState } from "@herefor/product-language";

/** One thing this person has agreed to, as their own screen reads it. */
export interface MyCommitmentRowView {
  readonly commitmentId: string;
  readonly supportNeedId: string;
  readonly label: string;
  /** `/c/<ref>/needs/<id>` — opaque identifiers only, never anything about a person. */
  readonly href: string;
  readonly dateLabel: string;
  /** The SUPPORT window. The hours they agreed to (NED-002). */
  readonly windowLabel: string;
  /** The appointment this help surrounds, if there is one. Context, never the headline. */
  readonly eventTitle: string | null;
  /** The OTHER people helping. The viewer is never in their own "with" list. */
  readonly alsoHelping: readonly string[];
  /** The need's server-derived state, projected. Never recomputed here (COV-002). */
  readonly stateKey: UserVisibleState;
  /** Sort key only; the surfaces render labels. */
  readonly startsAt: number;
}

/**
 * The viewer's own listed times, summarised.
 *
 * A summary rather than the editor: the full availability screen owns adding and
 * changing times, and reproducing its controls here would be two places to change one
 * thing. This says how far the times reach and offers the door.
 */
export interface MyAvailabilitySummaryView {
  readonly windowCount: number;
  /** The last local date their listed times cover. */
  readonly untilDateLabel: string;
  /** Whether that date has already passed. */
  readonly expired: boolean;
  /**
   * Inside the circle's renewal horizon, so the Epic 5 prompt belongs on screen.
   *
   * The horizon is a circle SETTING resolved by the server, not a constant chosen
   * here — the same number `AvailabilityRoute` uses, so the two surfaces cannot
   * disagree about when a week is ending soon.
   */
  readonly endingSoon: boolean;
}

export interface MyWeekContext {
  readonly timeZone: string;
  readonly publicPlanRef: string;
  readonly viewerPersonId: string;
  /** `Date.now()`, passed in so every projection here is testable and stable. */
  readonly now: number;
  /** The circle's availability renewal horizon, in local days (Epic 5, AVL-019). */
  readonly renewalHorizonDays: number;
}

/**
 * What this person has agreed to, soonest first.
 *
 * NOT WINDOWED TO SEVEN DAYS, despite the screen's name. A commitment nine days out
 * is still a commitment somebody made, and a list that silently dropped it at a
 * boundary would teach people that this screen is not the whole picture — which would
 * cost more than the extra row saves. "My week" is what the screen is FOR, not a
 * filter it applies.
 *
 * Past, done and cancelled agreements are left out: this is what is coming, and a
 * record of last Tuesday is history rather than something to read every morning.
 * The history itself is preserved — invariant 6 — it simply is not this list.
 */
export function toMyCommitmentRows(
  plan: LivePlanResult,
  commitments: readonly commitmentContracts.MyCommitmentView[],
  recheckSupportNeedIds: ReadonlySet<string>,
  context: MyWeekContext,
): readonly MyCommitmentRowView[] {
  const zone = timeZone(context.timeZone);
  const needsById = new Map(plan.needs.map((need) => [need.supportNeedId, need]));
  const eventTitlesById = new Map(plan.events.map((event) => [event.eventId, event.title]));

  const rows: MyCommitmentRowView[] = [];
  for (const commitment of commitments) {
    // Withdrawn, completed and cancelled commitments are not things coming up. Only a
    // live agreement belongs on a screen about what happens next.
    if (commitment.state !== "accepted" && commitment.state !== "reconfirmation_required") {
      continue;
    }
    if (recheckSupportNeedIds.has(commitment.supportNeedId)) continue;

    const need = needsById.get(commitment.supportNeedId);
    if (need === undefined) continue;

    const stateKey = userVisibleStateKey(need.coverage.status);
    if (need.coverage.isPast || stateKey === "done" || stateKey === "cancelled") continue;

    rows.push({
      commitmentId: commitment.commitmentId,
      supportNeedId: need.supportNeedId,
      label: need.label,
      href: `/c/${context.publicPlanRef}/needs/${need.supportNeedId}`,
      dateLabel: String(localDateOf(instant(need.startsAt), zone)),
      windowLabel: formatWindowInZone(zone, instant(need.startsAt), instant(need.endsAt)),
      eventTitle: eventTitleFor(need.eventId, eventTitlesById),
      // The OTHERS. A surface that told Ravi "with Ravi Blackwood" reads as a stranger
      // who happens to share their name — the same rule the need detail applies.
      alsoHelping: need.responsible
        .filter((person) => person.personId !== context.viewerPersonId)
        .map((person) => person.displayName),
      stateKey,
      startsAt: need.startsAt,
    });
  }

  // Soonest first, then by label so two commitments in one window keep a stable order
  // across re-renders rather than swapping under somebody's eye.
  return rows.sort(
    (left, right) => left.startsAt - right.startsAt || left.label.localeCompare(right.label),
  );
}

/**
 * The viewer's OWN listed times, as one summary line.
 *
 * Their own only: the plan read carries the whole circle's availability for the
 * everyone-view, and a helper's screen showing when other people are free would be
 * the organiser's question on the wrong surface.
 *
 * Null when they have listed nothing — the caller renders the empty state, because a
 * summary of nothing is not a summary.
 */
export function toMyAvailabilitySummary(
  plan: LivePlanResult,
  context: MyWeekContext,
): MyAvailabilitySummaryView | null {
  const zone = timeZone(context.timeZone);
  const mine = (plan.availability ?? []).filter((row) => row.personId === context.viewerPersonId);
  if (mine.length === 0) return null;

  // The batch that reaches furthest forward decides the horizon. Somebody who listed
  // next week and the week after is not "ending soon" because the earlier one lapses.
  const latestEndsAt = Math.max(...mine.map((row) => row.endsAt));
  const expiry = availabilityBatchExpiry(latestEndsAt, context.now, zone);

  return {
    windowCount: mine.length,
    untilDateLabel: String(expiry.endDateLocal),
    expired: expiry.expired,
    endingSoon: isWithinAvailabilityRenewalHorizon(
      String(expiry.endDateLocal),
      context.now,
      zone,
      context.renewalHorizonDays,
    ),
  };
}

/** The event's title, or null. An unknown event id is context we simply do not have. */
function eventTitleFor(
  eventId: string | undefined,
  titles: ReadonlyMap<string, string>,
): string | null {
  if (eventId === undefined) return null;
  return titles.get(eventId) ?? null;
}
